// @ts-nocheck

/* eslint-disable no-extra-semi */
// License: CC0 (no rights reserved).

// This is based on https://observablehq.com/@dgreensp/implementing-fractional-indexing

export const BASE_62_DIGITS =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

// `a` may be empty string, `b` is null or non-empty string.
// `a < b` lexicographically if `b` is non-null.
// no trailing zeros allowed.
// digits is a string such as '0123456789' for base 10.  Digits must be in
// ascending character code order!
/**
 * @param {string} a
 * @param {string | null | undefined} b
 * @param {string} digits
 * @returns {string}
 */
function midpoint(a, b, digits) {
  const zero = digits[0]
  if (b != null && a >= b) {
    throw new Error(a + ' >= ' + b)
  }
  if (a.slice(-1) === zero || (b && b.slice(-1) === zero)) {
    throw new Error('trailing zero')
  }
  if (b) {
    // remove longest common prefix.  pad `a` with 0s as we
    // go.  note that we don't need to pad `b`, because it can't
    // end before `a` while traversing the common prefix.
    let n = 0
    while ((a[n] || zero) === b[n]) {
      n++
    }
    if (n > 0) {
      return b.slice(0, n) + midpoint(a.slice(n), b.slice(n), digits)
    }
  }
  // first digits (or lack of digit) are different
  const digitA = a ? digits.indexOf(a[0]) : 0
  const digitB = b != null ? digits.indexOf(b[0]) : digits.length
  if (digitB - digitA > 1) {
    const midDigit = Math.round(0.5 * (digitA + digitB))
    return digits[midDigit]
  } else {
    // first digits are consecutive
    if (b && b.length > 1) {
      return b.slice(0, 1)
    } else {
      // `b` is null or has length 1 (a single digit).
      // the first digit of `a` is the previous digit to `b`,
      // or 9 if `b` is null.
      // given, for example, midpoint('49', '5'), return
      // '4' + midpoint('9', null), which will become
      // '4' + '9' + midpoint('', null), which is '495'
      return digits[digitA] + midpoint(a.slice(1), null, digits)
    }
  }
}

/**
 * @param {string} int
 * @return {void}
 */

function validateInteger(int) {
  if (int.length !== getIntegerLength(int[0])) {
    throw new Error('invalid integer part of order key: ' + int)
  }
}

/**
 * @param {string} head
 * @return {number}
 */

function getIntegerLength(head) {
  if (head >= 'a' && head <= 'z') {
    return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2
  } else if (head >= 'A' && head <= 'Z') {
    return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2
  } else {
    throw new Error('invalid order key head: ' + head)
  }
}

/**
 * @param {string} key
 * @return {string}
 */

function getIntegerPart(key) {
  const integerPartLength = getIntegerLength(key[0])
  if (integerPartLength > key.length) {
    throw new Error('invalid order key: ' + key)
  }
  return key.slice(0, integerPartLength)
}

/**
 * @param {string} key
 * @param {string} digits
 * @return {void}
 */

function validateOrderKey(key, digits) {
  if (key === 'A' + digits[0].repeat(26)) {
    throw new Error('invalid order key: ' + key)
  }
  // getIntegerPart will throw if the first character is bad,
  // or the key is too short.  we'd call it to check these things
  // even if we didn't need the result
  const i = getIntegerPart(key)
  const f = key.slice(i.length)
  if (f.slice(-1) === digits[0]) {
    throw new Error('invalid order key: ' + key)
  }
}

/**
 * Validates that the tiebreaker is at least 4 characters long
 * @param {string} tiebreaker
 * @return {string} The first 4 characters of the tiebreaker
 */
function validateAndNormalizeTiebreaker(tiebreaker) {
  if (!tiebreaker || typeof tiebreaker !== 'string' || tiebreaker.length < 4) {
    throw new Error('tiebreaker must be at least 4 characters long')
  }
  return tiebreaker.slice(0, 4)
}

/**
 * Extract the base key and tiebreaker from a combined key
 * @param {string} combinedKey
 * @return {[string, string | null]} [baseKey, tiebreaker]
 */
function extractBaseKeyAndTiebreaker(combinedKey) {
  if (!combinedKey) return [null, null]

  // If the key is less than 4 characters, it can't have a valid tiebreaker
  if (combinedKey.length < 4) {
    return [combinedKey, null]
  }

  return [combinedKey.slice(0, -4), combinedKey.slice(-4)]
}

// note that this may return null, as there is a largest integer
/**
 * @param {string} x
 * @param {string} digits
 * @return {string | null}
 */
function incrementInteger(x, digits) {
  validateInteger(x)
  const [head, ...digs] = x.split('')
  let carry = true
  for (let i = digs.length - 1; carry && i >= 0; i--) {
    const d = digits.indexOf(digs[i]) + 1
    if (d === digits.length) {
      digs[i] = digits[0]
    } else {
      digs[i] = digits[d]
      carry = false
    }
  }
  if (carry) {
    if (head === 'Z') {
      return 'a' + digits[0]
    }
    if (head === 'z') {
      return null
    }
    const h = String.fromCharCode(head.charCodeAt(0) + 1)
    if (h > 'a') {
      digs.push(digits[0])
    } else {
      digs.pop()
    }
    return h + digs.join('')
  } else {
    return head + digs.join('')
  }
}

// note that this may return null, as there is a smallest integer
/**
 * @param {string} x
 * @param {string} digits
 * @return {string | null}
 */

function decrementInteger(x, digits) {
  validateInteger(x)
  const [head, ...digs] = x.split('')
  let borrow = true
  for (let i = digs.length - 1; borrow && i >= 0; i--) {
    const d = digits.indexOf(digs[i]) - 1
    if (d === -1) {
      digs[i] = digits.slice(-1)
    } else {
      digs[i] = digits[d]
      borrow = false
    }
  }
  if (borrow) {
    if (head === 'a') {
      return 'Z' + digits.slice(-1)
    }
    if (head === 'A') {
      return null
    }
    const h = String.fromCharCode(head.charCodeAt(0) - 1)
    if (h < 'Z') {
      digs.push(digits.slice(-1))
    } else {
      digs.pop()
    }
    return h + digs.join('')
  } else {
    return head + digs.join('')
  }
}

// `a` is an order key or null (START).
// `b` is an order key or null (END).
// `a < b` lexicographically if both are non-null.
// digits is a string such as '0123456789' for base 10.  Digits must be in
// ascending character code order!
/**
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @param {string=} digits
 * @param {string} tiebreaker - A string (at least 4 characters) used as a tiebreaker for deterministic ordering
 * @return {string}
 */
export function generateKeyBetween(a, b, digits = BASE_62_DIGITS, tiebreaker) {
  // Validate and normalize the tiebreaker (get first 4 chars)
  const normalizedTiebreaker = validateAndNormalizeTiebreaker(tiebreaker)

  // Handle null inputs
  if (a === b && a !== null) {
    throw new Error(`Cannot generate a key between identical values: ${a}`)
  }

  // Extract base key and tiebreakers from inputs
  let baseA = null,
    baseB = null
  let tiebreakerA = null,
    tiebreakerB = null

  if (a != null) {
    ;[baseA, tiebreakerA] = extractBaseKeyAndTiebreaker(a)
    validateOrderKey(baseA, digits)
  }

  if (b != null) {
    ;[baseB, tiebreakerB] = extractBaseKeyAndTiebreaker(b)
    validateOrderKey(baseB, digits)
  }

  // If base keys are equal, use tiebreakers to determine order
  if (baseA === baseB && baseA !== null) {
    // If both keys have tiebreakers
    if (tiebreakerA && tiebreakerB) {
      // If tiebreakers are identical, we need to generate a new key
      if (tiebreakerA === tiebreakerB) {
        throw new Error(`Identical keys with same tiebreaker: ${a}`)
      }

      // If tiebreakers are lexicographically positioned such that our new
      // tiebreaker falls between them, we can use the same base
      if (
        tiebreakerA < normalizedTiebreaker &&
        normalizedTiebreaker < tiebreakerB
      ) {
        return baseA + normalizedTiebreaker
      }

      // Otherwise, we need to generate a new base key
      if (tiebreakerA >= normalizedTiebreaker) {
        // Our tiebreaker comes before or equals tiebreakerA, so we need a key before baseA
        b = baseA + tiebreakerA
        a = null
      } else {
        // Our tiebreaker comes after tiebreakerB, so we need a key after baseB
        a = baseB + tiebreakerB
        b = null
      }
    } else {
      // At least one key doesn't have a tiebreaker (legacy key)
      // Generate a new base key with the proper tiebreaker to maintain ordering
      const newBaseKey = baseA + midpoint('', '1', digits)
      return newBaseKey + normalizedTiebreaker
    }

    // Re-extract since we've modified a and b
    if (a != null) {
      ;[baseA, tiebreakerA] = extractBaseKeyAndTiebreaker(a)
    } else {
      baseA = null
      tiebreakerA = null
    }

    if (b != null) {
      ;[baseB, tiebreakerB] = extractBaseKeyAndTiebreaker(b)
    } else {
      baseB = null
      tiebreakerB = null
    }
  }

  // Check if baseA and baseB are in correct order
  if (baseA != null && baseB != null && baseA >= baseB) {
    throw new Error(`Base keys out of order: ${baseA} >= ${baseB}`)
  }

  // Rest of the function to generate the base key
  let result

  if (baseA == null) {
    if (baseB == null) {
      result = 'a' + digits[0]
    } else {
      const ib = getIntegerPart(baseB)
      const fb = baseB.slice(ib.length)
      if (ib === 'A' + digits[0].repeat(26)) {
        result = ib + midpoint('', fb, digits)
      } else if (ib < baseB) {
        result = ib
      } else {
        const res = decrementInteger(ib, digits)
        if (res == null) {
          throw new Error(
            'Cannot decrement any more - reached minimum possible value'
          )
        }
        result = res
      }
    }
  } else if (baseB == null) {
    const ia = getIntegerPart(baseA)
    const fa = baseA.slice(ia.length)
    const i = incrementInteger(ia, digits)
    result = i == null ? ia + midpoint(fa, null, digits) : i
  } else {
    const ia = getIntegerPart(baseA)
    const fa = baseA.slice(ia.length)
    const ib = getIntegerPart(baseB)
    const fb = baseB.slice(ib.length)
    if (ia === ib) {
      result = ia + midpoint(fa, fb, digits)
    } else {
      const i = incrementInteger(ia, digits)
      if (i == null) {
        throw new Error(
          'Cannot increment any more - reached maximum possible value'
        )
      }
      if (i < baseB) {
        result = i
      } else {
        result = ia + midpoint(fa, null, digits)
      }
    }
  }

  // Append the normalized tiebreaker to the result
  return result + normalizedTiebreaker
}

/**
 * same preconditions as generateKeysBetween.
 * n >= 0.
 * Returns an array of n distinct keys in sorted order.
 * If a and b are both null, returns [a0, a1, ...]
 * If one or the other is null, returns consecutive "integer"
 * keys.  Otherwise, returns relatively short keys between
 * a and b.
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @param {number} n
 * @param {string=} digits
 * @param {string} tiebreaker - A string (at least 4 characters) used as a tiebreaker for deterministic ordering
 * @return {string[]}
 */
export function generateNKeysBetween(
  a,
  b,
  n,
  digits = BASE_62_DIGITS,
  tiebreaker
) {
  // Validate the tiebreaker
  const normalizedTiebreaker = validateAndNormalizeTiebreaker(tiebreaker)

  if (n === 0) {
    return []
  }
  if (n === 1) {
    return [generateKeyBetween(a, b, digits, normalizedTiebreaker)]
  }

  if (b == null) {
    let c = generateKeyBetween(a, b, digits, normalizedTiebreaker)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = generateKeyBetween(c, b, digits, normalizedTiebreaker)
      result.push(c)
    }
    return result
  }
  if (a == null) {
    let c = generateKeyBetween(a, b, digits, normalizedTiebreaker)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = generateKeyBetween(a, c, digits, normalizedTiebreaker)
      result.push(c)
    }
    result.reverse()
    return result
  }

  const mid = Math.floor(n / 2)
  const c = generateKeyBetween(a, b, digits, normalizedTiebreaker)

  return [
    ...generateNKeysBetween(a, c, mid, digits, normalizedTiebreaker),
    c,
    ...generateNKeysBetween(c, b, n - mid - 1, digits, normalizedTiebreaker),
  ]
}
